/**
 * Normalize MessagePack positional / abbreviated hub payloads (and legacy named JSON)
 * into the camelCase shapes GameApp already consumes.
 */

export const ACTION_STATUS = {
  SUCCESS: 200,
  REJECTED: 400,
  TOKEN_REJECTED: 401,
  INSUFFICIENT_BALANCE: 402,
  GAME_UNAVAILABLE: 404,
  SERVER_FAILURE: 500,
};

export class HubActionError extends Error {
  /**
   * @param {number} actionStatus
   * @param {string} [message]
   */
  constructor(actionStatus, message) {
    super(message || `Hub action failed (${actionStatus})`);
    this.name = 'HubActionError';
    this.actionStatus = actionStatus;
  }
}

/**
 * Missing hub flags stay null so a later game can tell "not sent" from an explicit false.
 * @param {unknown} value
 * @returns {boolean | null}
 */
export function optionalHubBool(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value !== 0 : null;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0') return false;
    return null;
  }
  return null;
}

/** @param {unknown} v */
export function hubNum(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/**
 * @param {unknown} raw
 * @param {number} i
 * @param {string} name
 */
export function hubAt(raw, i, name) {
  if (Array.isArray(raw)) return raw[i];
  if (raw && typeof raw === 'object') {
    const obj = /** @type {Record<string, unknown>} */ (raw);
    if (name in obj) return obj[name];
    const pascal = name.charAt(0).toUpperCase() + name.slice(1);
    if (pascal in obj) return obj[pascal];
  }
  return undefined;
}

/**
 * Packed `int[,]` → nested reel columns: `[reels, rows, flat]` or legacy nested arrays.
 * @param {unknown} value
 * @returns {number[][]}
 */
export function toMatrix(value) {
  if (!Array.isArray(value)) return [];
  const packed =
    value.length === 3 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number' &&
    Array.isArray(value[2]);
  if (!packed) {
    return value.map((row) =>
      Array.isArray(row) ? row.map((cell) => hubNum(cell)) : [],
    );
  }
  const [reels, rows, flat] = value;
  return Array.from({ length: reels }, (_, i) =>
    flat.slice(i * rows, (i + 1) * rows).map((cell) => hubNum(cell)),
  );
}

/**
 * @param {unknown} raw
 * @returns {{
 *   symbol: number,
 *   line: number,
 *   count: number,
 *   lineOdd: number,
 *   multiplier: number,
 *   lineWinAmount: number,
 *   lineWinCoinAmount: number,
 * }}
 */
export function decodeWinLine(raw) {
  if (Array.isArray(raw)) {
    return {
      symbol: hubNum(raw[0]),
      line: hubNum(raw[1]),
      count: hubNum(raw[2]),
      lineOdd: hubNum(raw[3]),
      multiplier: hubNum(raw[4]),
      lineWinAmount: hubNum(raw[5]),
      lineWinCoinAmount: hubNum(raw[6]),
    };
  }
  const o = raw && typeof raw === 'object' ? /** @type {Record<string, unknown>} */ (raw) : {};
  return {
    symbol: hubNum(o.symbol ?? o.Symbol),
    line: hubNum(o.line ?? o.Line),
    count: hubNum(o.count ?? o.Count),
    lineOdd: hubNum(o.lineOdd ?? o.LineOdd),
    multiplier: hubNum(o.multiplier ?? o.Multiplier),
    lineWinAmount: hubNum(o.lineWinAmount ?? o.LineWinAmount),
    lineWinCoinAmount: hubNum(o.lineWinCoinAmount ?? o.LineWinCoinAmount),
  };
}

/**
 * @param {unknown} raw
 * @returns {{
 *   matrix: number[][],
 *   additional: {
 *     wins: ReturnType<typeof decodeWinLine>[],
 *     expandingWild: number[],
 *     expandingWildMultipliers: number[],
 *   },
 * }}
 */
export function decodeViewResult(raw) {
  if (raw == null || typeof raw !== 'object') {
    return {
      matrix: [],
      additional: { wins: [], expandingWild: [], expandingWildMultipliers: [] },
    };
  }
  const obj = /** @type {Record<string, unknown>} */ (raw);
  const a =
    (obj.a && typeof obj.a === 'object' ? obj.a : null) ??
    (obj.additional && typeof obj.additional === 'object' ? obj.additional : null) ??
    (obj.Additional && typeof obj.Additional === 'object' ? obj.Additional : null) ??
    {};
  const add = /** @type {Record<string, unknown>} */ (a);
  const winsRaw = add.w ?? add.wins ?? add.Wins ?? [];
  const exwRaw = add.exw ?? add.expandingWild ?? add.ExpandingWild ?? [];
  const exwmRaw =
    add.exwm ?? add.expandingWildMultipliers ?? add.ExpandingWildMultipliers ?? [];
  return {
    matrix: toMatrix(obj.m ?? obj.matrix ?? obj.Matrix),
    additional: {
      wins: (Array.isArray(winsRaw) ? winsRaw : []).map(decodeWinLine),
      expandingWild: (Array.isArray(exwRaw) ? exwRaw : []).map((v) => hubNum(v)),
      expandingWildMultipliers: (Array.isArray(exwmRaw) ? exwmRaw : []).map((v) =>
        hubNum(v),
      ),
    },
  };
}

/**
 * @param {unknown} raw
 * @returns {Record<string, unknown> | null}
 */
function decodeUnfinishedSpin(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    const win = hubNum(raw[1]);
    const view = decodeViewResult(raw[3]);
    return {
      balance: hubNum(raw[0]),
      win,
      winAmount: win,
      odd: hubNum(raw[2]),
      matrix: view.matrix,
      additional: view.additional,
      wins: view.additional.wins,
      pendingWin: win > 0 ? win : 0,
      needsCollect: win > 0,
    };
  }
  if (typeof raw !== 'object') return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const view = decodeViewResult(o.viewResult ?? o.ViewResult ?? o);
  const win = hubNum(o.win ?? o.Win ?? o.winAmount ?? o.WinAmount ?? o.pendingWin);
  const matrix =
    view.matrix.length > 0
      ? view.matrix
      : toMatrix(o.matrix ?? o.Matrix ?? o.initialBoard ?? o.InitialBoard);
  const additional =
    view.additional.wins.length || view.additional.expandingWild.length
      ? view.additional
      : {
          wins: (
            /** @type {unknown[]} */ (
              o.wins ?? o.Wins ?? o.additional?.wins ?? o.Additional?.Wins ?? []
            )
          ).map(decodeWinLine),
          expandingWild: (
            /** @type {unknown[]} */ (
              o.expandingWild ??
                o.ExpandingWild ??
                o.additional?.expandingWild ??
                o.Additional?.ExpandingWild ??
                []
            )
          ).map((v) => hubNum(v)),
          expandingWildMultipliers: (
            /** @type {unknown[]} */ (
              o.expandingWildMultipliers ??
                o.ExpandingWildMultipliers ??
                o.additional?.expandingWildMultipliers ??
                o.Additional?.ExpandingWildMultipliers ??
                []
            )
          ).map((v) => hubNum(v)),
        };
  return {
    ...o,
    balance: hubNum(o.balance ?? o.Balance),
    win,
    winAmount: win,
    odd: hubNum(o.odd ?? o.Odd),
    matrix,
    additional,
    wins: additional.wins,
    pendingWin: win > 0 ? win : hubNum(o.pendingWin ?? o.PendingWin),
    needsCollect:
      win > 0 ||
      o.needsCollect === true ||
      o.NeedsCollect === true ||
      o.awaitingCollect === true,
  };
}

/**
 * @param {unknown} currency
 * @returns {{ code: string | null, rate: number, precision: number | null }}
 */
function decodeCurrency(currency) {
  if (Array.isArray(currency)) {
    const code = currency[0] != null ? String(currency[0]).trim().toUpperCase() : null;
    return {
      code: code || null,
      rate: hubNum(currency[1]),
      precision:
        currency[2] != null && Number.isFinite(Number(currency[2]))
          ? Number(currency[2])
          : null,
    };
  }
  if (currency && typeof currency === 'object') {
    const o = /** @type {Record<string, unknown>} */ (currency);
    const codeRaw = o.code ?? o.Code ?? o.currencyCode ?? o.CurrencyCode;
    const code = codeRaw != null ? String(codeRaw).trim().toUpperCase() : null;
    return {
      code: code || null,
      rate: hubNum(o.rate ?? o.Rate),
      precision:
        o.precision != null && Number.isFinite(Number(o.precision))
          ? Number(o.precision)
          : o.Precision != null && Number.isFinite(Number(o.Precision))
            ? Number(o.Precision)
            : null,
    };
  }
  return { code: null, rate: 0, precision: null };
}

/**
 * Shared refusal envelope used by StartGameAction / GameAction failures:
 * named `{ result, actionType, responseCode, actionStatus }` or positional `[result, actionType, responseCode, actionStatus]`.
 * @param {unknown} raw
 * @returns {number | null}
 */
function readRefusalActionStatus(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    // Error frames are short and keep currency/betOptions slots as numbers, not arrays.
    if (
      raw.length === 4 &&
      !Array.isArray(raw[2]) &&
      !Array.isArray(raw[3]) &&
      typeof raw[2] === 'number' &&
      typeof raw[3] === 'number' &&
      (raw[0] == null || raw[1] == null)
    ) {
      return hubNum(raw[3]);
    }
    return null;
  }
  if (typeof raw !== 'object') return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const hasEnvelope =
    ('actionStatus' in o || 'ActionStatus' in o) &&
    ('responseCode' in o || 'ResponseCode' in o) &&
    ('result' in o || 'Result' in o || 'actionType' in o || 'ActionType' in o);
  if (!hasEnvelope) return null;
  // Successful StartGame payloads have balance/betOptions, not this envelope.
  if (
    o.balance != null ||
    o.Balance != null ||
    o.betOptions != null ||
    o.BetOptions != null ||
    o.quickBets != null ||
    o.QuickBets != null ||
    o.defaultBet != null ||
    o.DefaultBet != null ||
    o.defaultBetAmount != null ||
    o.currency != null ||
    o.Currency != null
  ) {
    return null;
  }
  const status = o.actionStatus ?? o.ActionStatus;
  return status == null ? null : hubNum(status);
}

/**
 * Paytable row from StartGameActionResult index 10.
 * Positional: `[symbol, kind, minCount, odds[]]` — odds are multiples of the selected bet.
 * @param {unknown} raw
 * @returns {{ symbol: number, kind: number, minCount: number, odds: number[] } | null}
 */
export function decodePaytableEntry(raw) {
  if (Array.isArray(raw)) {
    const oddsRaw = raw[3];
    return {
      symbol: hubNum(raw[0]),
      kind: hubNum(raw[1]),
      minCount: hubNum(raw[2]),
      odds: (Array.isArray(oddsRaw) ? oddsRaw : []).map((v) => hubNum(v)),
    };
  }
  if (!raw || typeof raw !== 'object') return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const oddsRaw = o.odds ?? o.Odds;
  return {
    symbol: hubNum(o.symbol ?? o.Symbol),
    kind: hubNum(o.kind ?? o.Kind),
    minCount: hubNum(o.minCount ?? o.MinCount),
    odds: (Array.isArray(oddsRaw) ? oddsRaw : []).map((v) => hubNum(v)),
  };
}

/**
 * @param {unknown} raw
 * @returns {{ symbol: number, kind: number, minCount: number, odds: number[] }[]}
 */
export function decodePaytable(raw) {
  if (!Array.isArray(raw)) return [];
  /** @type {{ symbol: number, kind: number, minCount: number, odds: number[] }[]} */
  const out = [];
  for (const entry of raw) {
    const decoded = decodePaytableEntry(entry);
    if (decoded) out.push(decoded);
  }
  return out;
}

/**
 * @param {unknown} raw
 * @returns {Record<string, unknown>}
 */
export function decodeStartGameActionResult(raw) {
  const refusalStatus = readRefusalActionStatus(raw);
  if (refusalStatus != null) {
    return {
      actionStatus: refusalStatus,
      balance: 0,
      currencyCode: null,
      currencyRate: 0,
      precision: null,
      betOptions: [],
      quickBets: [],
      defaultBet: 0,
      defaultBetAmount: 0,
      maxOdd: 0,
      matrix: [],
      initialBoard: [],
      buyBonusAvailable: false,
      unfinishedSpin: null,
      unfinishedBonus: null,
      paytable: [],
      additional: { wins: [], expandingWild: [], expandingWildMultipliers: [] },
      wins: [],
      pendingWin: 0,
      needsCollect: false,
      winAmount: 0,
    };
  }

  const statusRaw = hubAt(raw, 0, 'actionStatus') ?? hubAt(raw, 0, 'ActionStatus');
  const actionStatus = statusRaw == null ? ACTION_STATUS.SERVER_FAILURE : hubNum(statusRaw);
  const balance = hubNum(hubAt(raw, 1, 'balance') ?? hubAt(raw, 1, 'Balance'));
  const currency = decodeCurrency(hubAt(raw, 2, 'currency') ?? hubAt(raw, 2, 'Currency'));
  const betOptionsRaw = hubAt(raw, 3, 'betOptions') ?? hubAt(raw, 3, 'BetOptions');
  const legacyQuick =
    hubAt(raw, 3, 'quickBets') ??
    hubAt(raw, 3, 'QuickBets') ??
    (raw && typeof raw === 'object'
      ? /** @type {Record<string, unknown>} */ (raw).quickBets ??
        /** @type {Record<string, unknown>} */ (raw).QuickBets
      : undefined);
  const betSource = Array.isArray(betOptionsRaw)
    ? betOptionsRaw
    : Array.isArray(legacyQuick)
      ? legacyQuick
      : [];
  const quickBets = betSource.map((v) => hubNum(v)).filter((n) => Number.isFinite(n));
  const defaultBet = hubNum(
    hubAt(raw, 4, 'defaultBet') ??
      hubAt(raw, 4, 'DefaultBet') ??
      hubAt(raw, 4, 'defaultBetAmount') ??
      hubAt(raw, 4, 'DefaultBetAmount'),
  );
  const maxOdd = hubNum(hubAt(raw, 5, 'maxOdd') ?? hubAt(raw, 5, 'MaxOdd'));
  const initialBoard =
    hubAt(raw, 6, 'initialBoard') ??
    hubAt(raw, 6, 'InitialBoard') ??
    hubAt(raw, 6, 'matrix') ??
    hubAt(raw, 6, 'Matrix');
  const matrix = toMatrix(initialBoard);
  const buyBonusAvailable = optionalHubBool(
    hubAt(raw, 7, 'buyBonusAvailable') ??
      hubAt(raw, 7, 'BuyBonusAvailable') ??
      hubAt(raw, 7, 'HasBuyBonus'),
  );
  const unfinishedSpin = decodeUnfinishedSpin(
    hubAt(raw, 8, 'unfinishedSpin') ??
      hubAt(raw, 8, 'UnfinishedSpin') ??
      hubAt(raw, 8, 'SpinInfo'),
  );
  const unfinishedBonus =
    hubAt(raw, 9, 'unfinishedBonus') ??
    hubAt(raw, 9, 'UnfinishedBonus') ??
    hubAt(raw, 9, 'BonusSpinInfo') ??
    null;
  const paytable = decodePaytable(
    hubAt(raw, 10, 'paytable') ?? hubAt(raw, 10, 'Paytable') ?? hubAt(raw, 10, 'PayTable'),
  );

  /** Prefer unfinished spin board when restoring a round. */
  const displayMatrix =
    unfinishedSpin && Array.isArray(unfinishedSpin.matrix) && unfinishedSpin.matrix.length
      ? unfinishedSpin.matrix
      : matrix;
  const additional =
    unfinishedSpin && unfinishedSpin.additional
      ? unfinishedSpin.additional
      : { wins: [], expandingWild: [], expandingWildMultipliers: [] };

  return {
    actionStatus,
    balance,
    currencyCode:
      currency.code ??
      (typeof hubAt(raw, 2, 'currencyCode') === 'string'
        ? String(hubAt(raw, 2, 'currencyCode')).trim().toUpperCase()
        : null) ??
      (typeof hubAt(raw, 2, 'CurrencyCode') === 'string'
        ? String(hubAt(raw, 2, 'CurrencyCode')).trim().toUpperCase()
        : null),
    currencyRate: currency.rate,
    precision:
      currency.precision ??
      (Number.isFinite(Number(hubAt(raw, 2, 'precision')))
        ? Number(hubAt(raw, 2, 'precision'))
        : null) ??
      (Number.isFinite(Number(hubAt(raw, 2, 'Precision')))
        ? Number(hubAt(raw, 2, 'Precision'))
        : null),
    betOptions: betSource.map(String),
    quickBets,
    defaultBet,
    defaultBetAmount: defaultBet,
    maxOdd,
    matrix: displayMatrix,
    initialBoard: matrix,
    buyBonusAvailable,
    unfinishedSpin,
    unfinishedBonus,
    paytable,
    additional,
    wins: additional.wins,
    pendingWin: unfinishedSpin?.pendingWin ?? 0,
    needsCollect: unfinishedSpin?.needsCollect === true,
    winAmount: unfinishedSpin?.winAmount ?? 0,
  };
}

/**
 * @param {unknown} raw
 * @returns {Record<string, unknown>}
 */
export function decodeGameActionResult(raw) {
  const actionStatus = hubNum(
    hubAt(raw, 3, 'actionStatus') ?? hubAt(raw, 3, 'ActionStatus'),
  );
  const actionType = hubAt(raw, 1, 'actionType') ?? hubAt(raw, 1, 'ActionType') ?? null;
  const responseCode = hubNum(
    hubAt(raw, 2, 'responseCode') ?? hubAt(raw, 2, 'ResponseCode'),
  );
  const body = hubAt(raw, 0, 'result') ?? hubAt(raw, 0, 'Result');

  if (body == null) {
    return {
      actionStatus,
      actionType,
      responseCode,
      result: null,
    };
  }

  // Legacy flat spin result (named JSON): balance/winAmount + nested Result.matrix
  const legacyNested =
    body &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    ((/** @type {Record<string, unknown>} */ (body).result != null &&
      typeof /** @type {Record<string, unknown>} */ (body).result === 'object') ||
      (/** @type {Record<string, unknown>} */ (body).Result != null &&
        typeof /** @type {Record<string, unknown>} */ (body).Result === 'object') ||
      /** @type {Record<string, unknown>} */ (body).matrix != null ||
      /** @type {Record<string, unknown>} */ (body).Matrix != null);

  if (Array.isArray(body) || !legacyNested) {
    const view = decodeViewResult(hubAt(body, 3, 'viewResult') ?? hubAt(body, 3, 'ViewResult'));
    const balance = hubNum(hubAt(body, 0, 'balance') ?? hubAt(body, 0, 'Balance'));
    const win = hubNum(
      hubAt(body, 1, 'win') ??
        hubAt(body, 1, 'Win') ??
        hubAt(body, 1, 'winAmount') ??
        hubAt(body, 1, 'WinAmount'),
    );
    const odd = hubNum(hubAt(body, 2, 'odd') ?? hubAt(body, 2, 'Odd'));
    const spinBody = {
      balance,
      win,
      winAmount: win,
      odd,
      matrix: view.matrix,
      additional: view.additional,
      viewResult: view,
    };
    return {
      actionStatus,
      actionType,
      responseCode,
      result: spinBody,
      balance,
      winAmount: win,
    };
  }

  const named = /** @type {Record<string, unknown>} */ (body);
  const nested = named.result ?? named.Result ?? named;
  const view = decodeViewResult(
    nested && typeof nested === 'object'
      ? /** @type {Record<string, unknown>} */ (nested).viewResult ??
          /** @type {Record<string, unknown>} */ (nested).ViewResult ??
          nested
      : nested,
  );
  const balance = hubNum(named.balance ?? named.Balance);
  const win = hubNum(named.win ?? named.Win ?? named.winAmount ?? named.WinAmount);
  const odd = hubNum(named.odd ?? named.Odd);
  const matrix =
    view.matrix.length > 0
      ? view.matrix
      : toMatrix(
          nested && typeof nested === 'object'
            ? /** @type {Record<string, unknown>} */ (nested).matrix ??
                /** @type {Record<string, unknown>} */ (nested).Matrix
            : null,
        );
  const additional =
    view.additional.wins.length || view.additional.expandingWild.length
      ? view.additional
      : decodeViewResult(nested).additional;
  const spinBody = {
    balance,
    win,
    winAmount: win,
    odd,
    matrix,
    additional,
    viewResult: { matrix, additional },
  };
  return {
    actionStatus,
    actionType,
    responseCode,
    result: spinBody,
    balance,
    winAmount: win,
  };
}

/**
 * @param {unknown} raw
 * @returns {Record<string, unknown>}
 */
export function decodeGetBalanceResult(raw) {
  return {
    balance: hubNum(hubAt(raw, 0, 'balance') ?? hubAt(raw, 0, 'Balance')),
    responseCode: hubNum(hubAt(raw, 1, 'responseCode') ?? hubAt(raw, 1, 'ResponseCode')),
  };
}
